import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  useTheme,
  useMediaQuery,
  Fade,
  Avatar,
  InputAdornment,
  IconButton,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  CheckCircleOutline,
  ErrorOutline
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import LanguageSwitcher from '../components/LanguageSwitcher';

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);

  // Password strength calculation
  const calculatePasswordStrength = (pass) => {
    let strength = 0;
    if (pass.length >= 8) strength += 25;
    if (/[a-z]/.test(pass)) strength += 25;
    if (/[A-Z]/.test(pass)) strength += 25;
    if (/\d/.test(pass)) strength += 12.5;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pass)) strength += 12.5;
    return Math.min(strength, 100);
  };

  const passwordStrength = calculatePasswordStrength(password);

  const getStrengthColor = () => {
    if (passwordStrength < 50) return 'error';
    if (passwordStrength < 75) return 'warning';
    return 'success';
  };

  const getStrengthLabel = () => {
    if (passwordStrength < 50) return t('auth:resetPassword.strength.weak');
    if (passwordStrength < 75) return t('auth:resetPassword.strength.medium');
    return t('auth:resetPassword.strength.strong');
  };

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const lang = searchParams.get('lang') || i18n.language;

        await axios.get(`${API_URL}/auth/reset-password/${token}`, {
          headers: {
            'Accept-Language': lang
          }
        });

        setTokenValid(true);
      } catch (err) {
        setValidationError(
          err.response?.data?.message ||
          t('auth:resetPassword.errors.invalidToken')
        );
      } finally {
        setIsValidating(false);
      }
    };

    if (token) {
      validateToken();
    } else {
      setValidationError(t('auth:resetPassword.errors.noToken'));
      setIsValidating(false);
    }
  }, [token, i18n.language, t, searchParams]);

  const validatePassword = () => {
    // Check if passwords match
    if (password !== confirmPassword) {
      return t('auth:resetPassword.errors.passwordMismatch');
    }

    // Check password strength requirements
    if (password.length < 8) {
      return t('auth:resetPassword.errors.minLength');
    }
    if (!/[a-z]/.test(password)) {
      return t('auth:resetPassword.errors.lowercase');
    }
    if (!/[A-Z]/.test(password)) {
      return t('auth:resetPassword.errors.uppercase');
    }
    if (!/\d/.test(password)) {
      return t('auth:resetPassword.errors.number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return t('auth:resetPassword.errors.special');
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationErr = validatePassword();
    if (validationErr) {
      setError(validationErr);
      return;
    }

    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await axios.post(
        `${API_URL}/auth/reset-password/${token}`,
        { password },
        {
          headers: {
            'Accept-Language': i18n.language
          }
        }
      );

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      // Backend returns error in 'error' field, not 'message'
      const errorMessage = err.response?.data?.error || err.response?.data?.message;
      const errorKey = err.response?.data?.errorKey;

      // If we have an errorKey, try to translate it
      if (errorKey) {
        // Backend sends keys like "passwordReset:samePassword"
        // Frontend needs "errors:passwordReset.samePassword" (with namespace)
        const frontendKey = 'errors:' + errorKey.replace(':', '.');
        const translatedError = t(frontendKey);

        // Only use translation if it's not the same as the key (meaning translation exists)
        if (translatedError !== frontendKey) {
          setError(translatedError);
        } else {
          setError(errorMessage || t('auth:resetPassword.errors.resetFailed'));
        }
      } else {
        setError(errorMessage || t('auth:resetPassword.errors.resetFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isValidating) {
    return (
      <Box sx={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: theme => theme.palette.background.default
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <img src="/logo512.png" alt="MetuMotion Logo" width="120" height="120" />
          </motion.div>
          <Typography variant="h6" sx={{ mt: 2 }}>{t('common:loading.default')}</Typography>
        </Box>
      </Box>
    );
  }

  if (validationError) {
    return (
      <Box sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: theme => theme.palette.background.default,
        overflow: 'hidden',
        position: 'relative'
      }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${theme.palette.primary[900]} 0%, ${theme.palette.primary[800]} 35%, ${theme.palette.primary[700]} 100%)`,
            opacity: 0.95,
            zIndex: 0
          }}
        />

        <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 3 }}>
          <LanguageSwitcher variant="full" />
        </Box>

        <Container maxWidth="sm" sx={{ zIndex: 2, my: 4, display: 'flex', alignItems: 'center' }}>
          <Fade in timeout={800}>
            <Paper
              elevation={24}
              sx={{
                width: '100%',
                borderRadius: 3,
                p: { xs: 3, sm: 5 },
                backdropFilter: 'blur(10px)',
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                textAlign: 'center'
              }}
            >
              <ErrorOutline sx={{ fontSize: 80, color: theme.palette.error.main, mb: 2 }} />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.error.main }}>
                {t('auth:resetPassword.errors.invalidTokenTitle')}
              </Typography>
              <Alert severity="error" sx={{ mb: 3 }}>
                {validationError}
              </Alert>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/forgot-password')}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.accent.mint} 100%)`
                }}
              >
                {t('auth:resetPassword.buttons.requestNewLink')}
              </Button>
            </Paper>
          </Fade>
        </Container>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: theme => theme.palette.background.default,
        overflow: 'hidden',
        position: 'relative'
      }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${theme.palette.primary[900]} 0%, ${theme.palette.primary[800]} 35%, ${theme.palette.primary[700]} 100%)`,
            opacity: 0.95,
            zIndex: 0
          }}
        />

        <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 3 }}>
          <LanguageSwitcher variant="full" />
        </Box>

        <Container maxWidth="sm" sx={{ zIndex: 2, my: 4, display: 'flex', alignItems: 'center' }}>
          <Fade in timeout={800}>
            <Paper
              elevation={24}
              sx={{
                width: '100%',
                borderRadius: 3,
                p: { xs: 3, sm: 5 },
                backdropFilter: 'blur(10px)',
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                textAlign: 'center'
              }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <CheckCircleOutline sx={{ fontSize: 80, color: theme.palette.success.main, mb: 2 }} />
              </motion.div>

              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.primary.main }}>
                {t('auth:resetPassword.success.title')}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {t('auth:resetPassword.success.message')}
              </Typography>

              <Alert severity="success" sx={{ mb: 3 }}>
                {t('auth:resetPassword.success.redirecting')}
              </Alert>
            </Paper>
          </Fade>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      minHeight: '100vh',
      bgcolor: theme => theme.palette.background.default,
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Background gradient effect */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${theme.palette.primary[900]} 0%, ${theme.palette.primary[800]} 35%, ${theme.palette.primary[700]} 100%)`,
          opacity: 0.95,
          zIndex: 0
        }}
      />

      {/* Decorative circles */}
      <Fade in timeout={1000}>
        <Box
          sx={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: theme.palette.accent.mint,
            filter: 'blur(80px)',
            opacity: 0.2,
            top: '-50px',
            right: '-50px',
            zIndex: 1
          }}
        />
      </Fade>

      {/* Language Switcher */}
      <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 3 }}>
        <LanguageSwitcher variant="full" />
      </Box>

      <Container maxWidth="sm" sx={{ zIndex: 2, my: 4, display: 'flex', alignItems: 'center' }}>
        <Fade in timeout={800}>
          <Paper
            elevation={24}
            sx={{
              width: '100%',
              borderRadius: 3,
              p: { xs: 3, sm: 5 },
              backdropFilter: 'blur(10px)',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Mobile logo */}
            {isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <Avatar
                  src="/logo192.png"
                  alt="MetuMotion Logo"
                  sx={{
                    width: 80,
                    height: 80,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                />
              </Box>
            )}

            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <LockOutlined
                sx={{
                  fontSize: 60,
                  color: theme.palette.primary.main,
                  mb: 2
                }}
              />
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  color: theme.palette.primary.main
                }}
              >
                {t('auth:resetPassword.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('auth:resetPassword.subtitle')}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    required
                    fullWidth
                    name="password"
                    label={t('auth:resetPassword.form.password.label')}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 2 },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    helperText={t('auth:resetPassword.form.password.helperText')}
                  />

                  {password && (
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('auth:resetPassword.strength.label')}
                        </Typography>
                        <Chip
                          label={getStrengthLabel()}
                          size="small"
                          color={getStrengthColor()}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={passwordStrength}
                        color={getStrengthColor()}
                        sx={{ height: 6, borderRadius: 1 }}
                      />
                    </Box>
                  )}
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    required
                    fullWidth
                    name="confirmPassword"
                    label={t('auth:resetPassword.form.confirmPassword.label')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 2 },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 4 }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSubmitting || !password || !confirmPassword}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.accent.mint} 100%)`,
                    boxShadow: '0 4px 10px rgba(17, 138, 178, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 6px 15px rgba(17, 138, 178, 0.4)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  {isSubmitting
                    ? t('auth:resetPassword.buttons.resetting')
                    : t('auth:resetPassword.buttons.resetPassword')}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;
